import { RefObject, useLayoutEffect, useRef, useState } from "react";
import { useResizeObserver } from "./use-resize-observer.ts";

// NOTE: Taken from github.com/Eliav2/react-responsive-overflow-list but not added as dependency because we
// only use a subset of functionality

// Technical details:
// Uses a three-phases approach:
// 1. "measuring" renders all items to calculate positions,
// 2. "measuring overflow" render all items fit in the container,
//     - try to add the overflow indicator item to the container.
//     - check if it opens a new (flex-wrap) row, if so, remove the last item from the last row.
// 3. "normal" phase shows only what fits within constraints. (this is the stable state that we want to keep)

type OverflowListPhase = "normal" | "measuring" | "measuring-overflow-indicator";

type UseOverflowListOptions = {
    /** Total number of items the consumer wants to render. */
    itemCount: number;
};

type UseOverflowListReturn<TContainer extends HTMLElement = HTMLElement, TOverflow extends HTMLElement = HTMLElement> = {
    /** Attach to the flex-wrap container that holds the items. */
    containerRef: RefObject<TContainer | null>;
    /** Attach to the overflow indicator element so its width is measured. */
    overflowIndicatorRef: RefObject<TOverflow | null>;
    /** Final visible count = visibleCount - subtractCount (matches original primitive's `finalVisibleCount`). */
    visibleCount: number;
    /** Number of items hidden by the overflow indicator (items.length - visibleCount). */
    hiddenCount: number;
    /** Current measurement phase — consumers must use this to decide item visibility. */
    phase: OverflowListPhase;
    /**
     * True when the overflow indicator should be rendered. False during the initial
     * "measuring" phase so all items can be measured without the indicator interfering.
     */
    showOverflow: boolean;
};

export function useOverflowList<TContainer extends HTMLElement = HTMLElement, TOverflow extends HTMLElement = HTMLElement>({
    itemCount,
}: UseOverflowListOptions): UseOverflowListReturn<TContainer, TOverflow> {
    const [visibleCount, setVisibleCount] = useState(itemCount);
    const [subtractCount, setSubtractCount] = useState(0);
    const [phase, setPhase] = useState<OverflowListPhase>("normal");

    const containerRef = useRef<TContainer | null>(null);
    const overflowIndicatorRef = useRef<TOverflow | null>(null);

    const finalVisibleCount = visibleCount - subtractCount;
    const overflowCount = itemCount - finalVisibleCount;
    const showOverflow = overflowCount > 0 && phase !== "measuring";

    // Reset state when items change
    useLayoutEffect(() => {
        setPhase("measuring");
        setVisibleCount(itemCount);
        setSubtractCount(0);
    }, [itemCount]);

    useLayoutEffect(() => {
        // in measurement, evaluate results
        if (phase !== "measuring") return;

        const children = getFirstRowChildren(containerRef, overflowIndicatorRef);
        if (children !== null) {
            if (itemCount === 1) {
                const containerWidth = containerRef.current?.getBoundingClientRect().width ?? 0;
                const itemWidth = children[0].getBoundingClientRect().width ?? 0;
                setVisibleCount(itemWidth > containerWidth ? 0 : 1);
            } else {
                setVisibleCount(children.length);
            }
        }
        setPhase("measuring-overflow-indicator");
    }, [itemCount, phase]);

    useLayoutEffect(() => {
        // After placing the overflow indicator, evaluate if it ends up opening a new row
        if (phase !== "measuring-overflow-indicator") return;
        if (finalVisibleCount <= 0 || !overflowIndicatorRef.current) {
            setPhase("normal");
            return;
        }

        const children = getFirstRowChildren(containerRef, overflowIndicatorRef);
        if (children === null) {
            setPhase("normal");
            return;
        }

        const firstRowTop = children[0].getBoundingClientRect().top;
        const overflowTop = overflowIndicatorRef.current.getBoundingClientRect().top;

        if (Math.round(overflowTop) > Math.round(firstRowTop)) {
            setSubtractCount((c) => c + 1);
        } else {
            setPhase("normal");
        }
    }, [finalVisibleCount, phase, subtractCount]);

    // if the container dimensions change, re-measure
    useResizeObserver(containerRef, () => {
        if (phase === "normal") {
            setPhase("measuring");
            setSubtractCount(0);
        }
    });

    return {
        containerRef,
        overflowIndicatorRef,
        visibleCount: finalVisibleCount,
        hiddenCount: overflowCount,
        phase,
        showOverflow,
    };
}

function getFirstRowChildren(
    containerRef: RefObject<HTMLElement | null>,
    overflowRef: RefObject<HTMLElement | null>
): HTMLElement[] | null {
    if (!containerRef.current) return null;

    const children = Array.from(containerRef.current.children).filter((child) => overflowRef.current !== child) as HTMLElement[];

    if (children.length === 0) return null;

    /**
     * We let flex-warp determine when a child needs to "go the the next row".
     * Therefore, we compare the top of each child, which should be (roughly) the same for children on the same row
     * But, because of sub-pixel rendering and rounding issues, we check if the top is within 5px of the first child
     * to account for minor differences. Since a child is 32px high, this is fine.
     */
    const firstRowTop = Math.round(children[0].getBoundingClientRect().top);
    return children.filter((child) => Math.abs(child.getBoundingClientRect().top - firstRowTop) < 5);
}
